import { useEffect, useState, type ReactNode } from 'react'
import {
  ActionIcon,
  Button,
  Collapse,
  SegmentedControl,
  Select,
  Switch,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import { DateInput } from '@/components/ui/DateInput'
import {
  IconAlertCircle,
  IconChevronRight,
  IconCreditCard,
  IconInfoCircle,
  IconReceipt2,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ORPCError } from '@orpc/client'
import { useOrpc } from '@/hooks/useClient'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import { getInsurancePayersList } from '@/pages/Settings/insurancePlans.service'
import { formatCents } from '../utils'
import {
  breakdownError,
  breakdownFields,
  computeInsuranceCents,
  initialBreakdown,
  isEobAmountsValid,
  KIND_ORDER,
  type EobAmounts,
  type PatientInvoiceInfo,
} from './eob-amounts'
import { EobAmountsForm } from './EobAmountsForm'
import { EobAttachment } from './EobAttachment'
import { VccCardFields, type VccCardApi } from './VccCardFields'

// "Record insurance payment" (accounting-plan Task 20, mockups 02/03): the drawer's
// write side for payments received outside the system. Method-conditional fields per
// the client task — check: number + deposit date (the check date sent to the backend is the
// payment date — they were always the same, so the form asks once); ACH/EFT: trace ref + optional
// last4. The insurance payment amount is keyed in the EOB block (EobAmountsForm) — it
// is the amount being recorded, and for VCC also the amount being charged (one field,
// so the charge-equals-entered-payment rule holds by construction). The Payer field
// starts on the case insurance's payer and is picked from the Settings payer catalog.
//
// Check/ACH submit = one recordPayment call carrying the payment's own EOB figures
// (Task 26 — the amount is entered; defaults to the derived allowed − patient_resp; the
// backend re-validates against the claim's remaining charge). A 409 whose message asks
// to confirm the overpayment reopens as a confirmation panel — confirming re-posts with
// confirmOverpayment. Any other backend error shows verbatim.
//
// Virtual card (Task 21) = stripe.createPaymentMethod → vccCharge, with a
// per-charge "Process as MOTO" switch (default OFF). Declines and the 3DS-required
// failure surface the backend failure_reason inline (entered data kept — the card
// lives in Stripe iframes); { motoUnavailable } swaps the card fields for the
// Dashboard-charge fallback, whose link flips to recording the payment with
// method 'vcc' + the Stripe payment id as the reference. No overpayment confirmation
// for VCC — the backend hard-409s before charging.
const EMPTY_AMOUNTS: EobAmounts = {
  allowedCents: null,
  patientRespCents: null,
  insuranceCents: null,
  insuranceEdited: false,
}

const MOTO_TOOLTIP =
  'Skips 3DS verification. Enable if the charge fails with an authentication error ' +
  "(requires MOTO on the clinic's Stripe account)."

/** Quiet section card: icon chip + title header over the grouped fields. */
function FormSection({
  icon,
  title,
  children,
}: {
  icon: ReactNode
  title: string
  children: ReactNode
}) {
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
          {icon}
        </span>
        <Text size="sm" fw={600} c="dark">
          {title}
        </Text>
      </div>
      {children}
    </section>
  )
}

type RecordPaymentFormProps = {
  encounterId: string
  /** From the case detail reconciliation block. */
  providerChargeCents: number | null
  patientPaidCents: number | null
  /** Σ insurance already paid — the claim's remaining charge is charge − this. */
  insurancePaidCents: number
  patientInvoices: PatientInvoiceInfo[]
  /** The case insurance's payer — the Payer field's starting value. */
  payerName?: string
  /** Tells the drawer whether the form holds unsaved input (it asks before closing). */
  onDirtyChange?: (dirty: boolean) => void
  /** Case detail refreshed + back to the read view. */
  onSuccess: () => void
  onCancel: () => void
}

export function RecordPaymentForm({
  encounterId,
  providerChargeCents,
  patientPaidCents,
  insurancePaidCents,
  patientInvoices,
  payerName: initialPayerName,
  onDirtyChange,
  onSuccess,
  onCancel,
}: RecordPaymentFormProps) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const middlewareClient = useMiddlewareClient()

  const [amounts, setAmounts] = useState<EobAmounts>(EMPTY_AMOUNTS)
  const [breakdown, setBreakdown] = useState(() => initialBreakdown(patientInvoices))
  const [method, setMethod] = useState<'check' | 'ach_eft' | 'vcc'>('check')
  const [payerName, setPayerName] = useState(initialPayerName ?? '')
  const [paymentDate, setPaymentDate] = useState<string | null>(null)
  const [depositDate, setDepositDate] = useState<string | null>(null)
  const [checkNumber, setCheckNumber] = useState('')
  const [externalRef, setExternalRef] = useState('')
  const [accountLast4, setAccountLast4] = useState('')
  const [eobDocumentId, setEobDocumentId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [overpaymentPrompt, setOverpaymentPrompt] = useState<string | null>(null)
  const [useMoto, setUseMoto] = useState(false)
  /** "Process as MOTO" lives behind this progressive-disclosure toggle. */
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [vccApi, setVccApi] = useState<VccCardApi | null>(null)
  const [vccComplete, setVccComplete] = useState(false)
  const [charging, setCharging] = useState(false)
  /** The motoUnavailable fallback message; non-null replaces the card fields. */
  const [motoUnavailable, setMotoUnavailable] = useState<string | null>(null)
  /** Dashboard-charged VCC recorded manually (the motoUnavailable fallback link). */
  const [vccRecorded, setVccRecorded] = useState(false)

  const recordPayment = useMutation(orpc.billingV2RecordPayment.mutationOptions())
  const vccCharge = useMutation(orpc.billingV2VccCharge.mutationOptions())
  const payers = useQuery({
    queryKey: ['insurance-payers-list'],
    queryFn: () => getInsurancePayersList(middlewareClient),
  })

  // The payment stores the payer's name as a stamp (no payer id or state), so the catalog
  // collapses to one entry per name; the case insurance's payer stays selectable even
  // when the catalog does not list it.
  const payerNames = new Set((payers.data ?? []).map((payer) => payer.payerName))
  if (initialPayerName) payerNames.add(initialPayerName)
  const payerOptions = [...payerNames]

  // The payment reconciles against what is left of the claim, not the full charge
  // (each recorded payment's allowed eats into it).
  const remainingChargeCents =
    providerChargeCents === null ? null : providerChargeCents - insurancePaidCents

  const isVccCharge = method === 'vcc' && !vccRecorded
  const methodFieldsFilled =
    method === 'check'
      ? checkNumber.trim() !== ''
      : method === 'ach_eft' || vccRecorded
        ? externalRef.trim() !== ''
        : vccApi !== null && vccComplete
  const canSubmit =
    isEobAmountsValid(amounts) &&
    (amounts.insuranceCents ?? 0) > 0 &&
    payerName.trim() !== '' &&
    paymentDate !== null &&
    methodFieldsFilled &&
    motoUnavailable === null &&
    breakdownError(breakdown, patientInvoices, amounts.patientRespCents) === null
  const isSubmitting = recordPayment.isPending || charging

  // Any field away from its starting value — the drawer asks before closing over it.
  // Switching the method alone is not input; the prefilled payer and breakdown are not either.
  const startingBreakdown = initialBreakdown(patientInvoices)
  const isDirty =
    amounts.allowedCents !== null ||
    amounts.patientRespCents !== null ||
    amounts.insuranceCents !== null ||
    KIND_ORDER.some((kind) => breakdown[kind] !== startingBreakdown[kind]) ||
    payerName !== (initialPayerName ?? '') ||
    paymentDate !== null ||
    depositDate !== null ||
    checkNumber !== '' ||
    externalRef !== '' ||
    accountLast4 !== '' ||
    eobDocumentId !== null ||
    vccComplete ||
    useMoto

  useEffect(() => {
    onDirtyChange?.(isDirty)
    return () => onDirtyChange?.(false)
  }, [isDirty, onDirtyChange])

  const changeMethod = (value: 'check' | 'ach_eft' | 'vcc') => {
    setMethod(value)
    // Leaving and re-entering Virtual card always restarts from the charge flow.
    setMotoUnavailable(null)
    setVccRecorded(false)
    setError(null)

    if (value === 'vcc') {
      setAmounts((prev) => ({
        ...prev,
        insuranceEdited: false,
        insuranceCents: computeInsuranceCents(prev.allowedCents, prev.patientRespCents),
      }))
    }
  }

  const postPayment = async (confirmOverpayment: boolean) => {
    try {
      await recordPayment.mutateAsync({
        encounterId,
        method,
        // The payment carries its own EOB figures; amountCents is the entered payment;
        // defaults to allowed − patientResp.
        allowedCents: amounts.allowedCents ?? 0,
        patientRespCents: amounts.patientRespCents ?? 0,
        amountCents: amounts.insuranceCents ?? undefined,
        payerName: payerName.trim(),
        paymentDate: paymentDate ?? '',
        depositDate: method === 'check' ? (depositDate ?? undefined) : undefined,
        // For a recorded VCC the reference is the Stripe payment id (Dashboard charge).
        externalRef: method !== 'check' ? externalRef.trim() : undefined,
        checkNumber: method === 'check' ? checkNumber.trim() : undefined,
        checkDate: method === 'check' ? (paymentDate ?? undefined) : undefined,
        accountLast4:
          method === 'ach_eft' && accountLast4.trim() !== '' ? accountLast4.trim() : undefined,
        eobDocumentId: eobDocumentId ?? undefined,
        confirmOverpayment: confirmOverpayment ? true : undefined,
        ...breakdownFields(breakdown, patientInvoices),
      })
      void queryClient.invalidateQueries({ queryKey: orpc.billingV2CaseDetail.key() })
      void queryClient.invalidateQueries({ queryKey: orpc.billingV2ListCases.key() })
      onSuccess()
    } catch (err) {
      if (err instanceof ORPCError && err.status === 409) {
        // The posting service has exactly two 409s: the overpayment guard (which asks
        // for confirmation) and the duplicate-payment dedup (shown verbatim).
        if (err.message.includes('Confirm the overpayment')) {
          setOverpaymentPrompt(err.message)
          return
        }
      }
      setError(err instanceof Error ? err.message : 'Failed to record the payment')
    }
  }

  const chargeVcc = async () => {
    setCharging(true)
    try {
      // Only the Stripe-issued paymentMethodId leaves the iframes — no card data here.
      const paymentMethodId = await vccApi!.createPaymentMethod()
      const result = await vccCharge.mutateAsync({
        encounterId,
        paymentMethodId,
        allowedCents: amounts.allowedCents ?? 0,
        patientRespCents: amounts.patientRespCents ?? 0,
        payerName: payerName.trim(),
        paymentDate: paymentDate ?? '',
        eobDocumentId: eobDocumentId ?? undefined,
        useMoto,
      })
      if ('motoUnavailable' in result) {
        setMotoUnavailable(result.message)
        return
      }
      void queryClient.invalidateQueries({ queryKey: orpc.billingV2CaseDetail.key() })
      void queryClient.invalidateQueries({ queryKey: orpc.billingV2ListCases.key() })
      onSuccess()
    } catch (err) {
      // Declines and the 3DS-required failure arrive as a 422 whose message is the
      // failure_reason (already includes the retry-with-MOTO hint); the pre-charge
      // overpayment 409 and everything else show verbatim too. Entered data is kept.
      setError(err instanceof Error ? err.message : 'Failed to charge the card')
    } finally {
      setCharging(false)
    }
  }

  const handleSubmit = async () => {
    setError(null)
    setOverpaymentPrompt(null)
    if (isVccCharge) {
      await chargeVcc()
      return
    }
    await postPayment(false)
  }

  const handleConfirmOverpayment = async () => {
    setOverpaymentPrompt(null)
    await postPayment(true)
  }

  return (
    <div className="flex flex-col gap-4">
      <FormSection icon={<IconReceipt2 size={16} />} title="Claim & EOB amounts">
        <EobAmountsForm
          providerChargeCents={remainingChargeCents}
          patientPaidCents={patientPaidCents}
          value={amounts}
          onChange={setAmounts}
          insuranceDisabled={isVccCharge}
          patientInvoices={patientInvoices}
          breakdown={breakdown}
          onBreakdownChange={setBreakdown}
        />
        <EobAttachment encounterId={encounterId} onUploaded={setEobDocumentId} />
      </FormSection>

      <FormSection icon={<IconCreditCard size={16} />} title="Payment">
        <SegmentedControl
          fullWidth
          value={method}
          onChange={(value) => changeMethod(value as 'check' | 'ach_eft' | 'vcc')}
          data={[
            { value: 'check', label: 'Check' },
            { value: 'ach_eft', label: 'ACH / EFT' },
            { value: 'vcc', label: 'Virtual card' },
          ]}
        />
        {isVccCharge && !motoUnavailable ? (
          <TextInput
            label="Amount"
            description="Allowed − Patient responsibility"
            value={amounts.insuranceCents === null ? '' : formatCents(amounts.insuranceCents)}
            disabled
            classNames={{ input: '!text-slate-700 font-semibold' }}
          />
        ) : null}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Payer"
            placeholder={payers.isPending ? 'Loading payers...' : 'Select a payer'}
            data={payerOptions}
            value={payerName || null}
            onChange={(value) => setPayerName(value ?? '')}
            searchable
            error={payers.isError ? 'Failed to load payers' : undefined}
          />
          <DateInput
            label="Payment date"
            valueFormat="MM/DD/YYYY"
            value={paymentDate}
            onChange={setPaymentDate}
          />
        </div>
        {method === 'check' ? (
          <div className="grid grid-cols-2 gap-3">
            <TextInput
              label="Check number"
              value={checkNumber}
              onChange={(e) => setCheckNumber(e.currentTarget.value)}
            />
            <DateInput
              label="Deposit date"
              valueFormat="MM/DD/YYYY"
              value={depositDate}
              onChange={setDepositDate}
            />
          </div>
        ) : method === 'ach_eft' ? (
          <div className="grid grid-cols-2 gap-3">
            <TextInput
              label="EFT trace / remittance reference"
              value={externalRef}
              onChange={(e) => setExternalRef(e.currentTarget.value)}
            />
            <TextInput
              label="Account last 4"
              value={accountLast4}
              onChange={(e) => setAccountLast4(e.currentTarget.value)}
            />
          </div>
        ) : vccRecorded ? (
          <TextInput
            label="Stripe payment ID"
            description="From the charge made in the clinic's Stripe Dashboard"
            value={externalRef}
            onChange={(e) => setExternalRef(e.currentTarget.value)}
          />
        ) : motoUnavailable ? (
          <div className="flex flex-col gap-2 rounded-lg border border-sky-300 bg-sky-50 p-3">
            <Text size="sm" c="dark">
              {motoUnavailable}
            </Text>
            <div>
              <Button
                variant="light"
                size="xs"
                onClick={() => {
                  setMotoUnavailable(null)
                  setVccRecorded(true)
                }}
              >
                Record a Dashboard charge instead
              </Button>
            </div>
          </div>
        ) : (
          <>
            <VccCardFields
              encounterId={encounterId}
              onApiChange={setVccApi}
              onCompleteChange={setVccComplete}
            />
            <Text size="xs" c="dimmed">
              Card details are collected through Stripe and are not stored in BeHuman.
            </Text>
            <div className="flex flex-col gap-2 border-t border-slate-100 pt-2">
              <UnstyledButton
                onClick={() => setAdvancedOpen((prev) => !prev)}
                aria-expanded={advancedOpen}
                className="flex w-fit cursor-pointer items-center gap-1"
              >
                <IconChevronRight
                  size={14}
                  className={[
                    'text-slate-500 transition-transform duration-200',
                    advancedOpen ? 'rotate-90' : '',
                  ].join(' ')}
                />
                <Text size="xs" fw={600} c="dimmed">
                  Advanced
                </Text>
              </UnstyledButton>
              <Collapse in={advancedOpen} transitionDuration={200}>
                <div className="flex items-center gap-1 pl-5">
                  <Switch
                    size="sm"
                    label="Process as MOTO"
                    checked={useMoto}
                    onChange={(e) => setUseMoto(e.currentTarget.checked)}
                  />
                  <Tooltip label={MOTO_TOOLTIP} w={300} multiline>
                    <ActionIcon variant="subtle" color="gray" size="sm" aria-label="About MOTO">
                      <IconInfoCircle size={16} />
                    </ActionIcon>
                  </Tooltip>
                </div>
              </Collapse>
            </div>
          </>
        )}
      </FormSection>

      {error ? (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
          <IconAlertCircle size={16} className="mt-0.5 shrink-0 text-red-600" />
          <Text size="sm" c="red.8">
            {error}
          </Text>
        </div>
      ) : null}

      {overpaymentPrompt ? (
        <div className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
          <Text size="sm" c="dark">
            {overpaymentPrompt}
          </Text>
          <div className="flex justify-end gap-2">
            <Button variant="default" size="xs" onClick={() => setOverpaymentPrompt(null)}>
              Keep editing
            </Button>
            <Button
              size="xs"
              color="orange"
              loading={isSubmitting}
              onClick={handleConfirmOverpayment}
            >
              Record anyway
            </Button>
          </div>
        </div>
      ) : null}

      <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={!canSubmit} loading={isSubmitting} onClick={handleSubmit}>
          {isVccCharge ? 'Charge' : 'Record'} {formatCents(amounts.insuranceCents ?? 0)}
        </Button>
      </div>
    </div>
  )
}
